import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { Box, Button, CircularProgress, Typography } from "@mui/material"
import type React from "react"
import { Link as RouterLink, Navigate, useParams } from "react-router"
import { useShowPrecomputedBiothreatSignals } from "../../hooks/useFeatureFlags"
import { ROUTES } from "../../utils/constants"
import { extraColors } from "../../utils/theme"
import { MainContent } from "../MainContent"
import {
  GdeltClusterDrawerSummary,
  GdeltClusterMembersSection,
} from "./GdeltClusterDrawer"
import { NwssDrawerHeader, NwssSamplesSection } from "./NwssSignalDrawer"
import {
  commandSurfaceSx,
  DrawerVariantProvider,
} from "./precomputedDrawerStyles"
import type { PrecomputedBiothreatSignal } from "./types"
import {
  useBiothreatSignalById,
  type BiothreatSignalSource,
} from "./useBiothreatSignalById"
import { V2_SURFACE, V2_TEXT } from "./v2/biothreatV2Tokens"

/** Drawer content is fixed-width; cap the page so charts don't over-stretch. */
const CONTENT_MAX_WIDTH = 880

/**
 * Route wrapper for the biothreat signal detail page. The page renders the v2
 * "command" content, so it is only meaningful when the precomputed feature is
 * enabled — otherwise send the user back to the list.
 */
export const BiothreatSignalDetailGated: React.FC = () => {
  const showPrecomputed = useShowPrecomputedBiothreatSignals()
  if (!showPrecomputed) {
    return <Navigate replace to={ROUTES.BIOTHREAT_SIGNALS} />
  }
  return <BiothreatSignalDetailPage />
}

/**
 * Standalone, deep-linkable detail view for a single biothreat signal. Fetches
 * by source + id and reuses the drawer content blocks on the command surface.
 */
export const BiothreatSignalDetailPage: React.FC = () => {
  const { source, signalId } = useParams<{
    source: string
    signalId: string
  }>()
  const validSource = isBiothreatSource(source) ? source : null
  const { signal, isLoading, isError, isNotFound } = useBiothreatSignalById(
    validSource,
    signalId ?? null
  )

  return (
    <MainContent
      headerProps={{
        title: "Biothreat Signal",
        documentTitle: "Biothreat Signal",
      }}
      disableHorizontalGutters
      disableLeftGutter
    >
      <Box
        sx={{
          backgroundColor: V2_SURFACE.app,
          minHeight: "calc(100vh - 64px)",
          px: 3,
          py: 3,
        }}
      >
        <Box sx={{ maxWidth: CONTENT_MAX_WIDTH, mx: "auto" }}>
          <BackToSignalsLink />
          <Box
            sx={{
              ...commandSurfaceSx,
              position: "relative",
              borderRadius: "8px",
              p: 3,
              mt: 2,
            }}
          >
            <DrawerVariantProvider value="command">
              <BiothreatSignalDetailContent
                source={validSource}
                signal={signal}
                isLoading={isLoading}
                isError={isError}
                isNotFound={isNotFound || !validSource}
              />
            </DrawerVariantProvider>
          </Box>
        </Box>
      </Box>
    </MainContent>
  )
}

function isBiothreatSource(
  value: string | undefined
): value is BiothreatSignalSource {
  return value === "gdelt" || value === "nwss"
}

function BackToSignalsLink() {
  return (
    <Button
      component={RouterLink}
      to={ROUTES.BIOTHREAT_SIGNALS}
      startIcon={<ArrowBackIcon fontSize="small" />}
      sx={{ color: V2_TEXT.body, textTransform: "none", px: 0 }}
    >
      Back to Biothreat Signals
    </Button>
  )
}

function BiothreatSignalDetailContent({
  source,
  signal,
  isLoading,
  isError,
  isNotFound,
}: {
  source: BiothreatSignalSource | null
  signal: PrecomputedBiothreatSignal | undefined
  isLoading: boolean
  isError: boolean
  isNotFound: boolean
}) {
  if (isNotFound) {
    return (
      <DetailMessage color={extraColors.hint}>
        This biothreat signal could not be found.
      </DetailMessage>
    )
  }
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }
  if (isError || !signal || !source) {
    return (
      <DetailMessage color={extraColors.status.redLight}>
        Failed to load this biothreat signal. Please try again.
      </DetailMessage>
    )
  }
  return source === "gdelt" ? (
    <GdeltSignalDetail signal={signal} />
  ) : (
    <NwssSignalDetail signal={signal} />
  )
}

function GdeltSignalDetail({ signal }: { signal: PrecomputedBiothreatSignal }) {
  return (
    <>
      <GdeltClusterDrawerSummary cluster={signal} />
      <GdeltClusterMembersSection clusterId={signal.cluster_id} />
    </>
  )
}

function NwssSignalDetail({ signal }: { signal: PrecomputedBiothreatSignal }) {
  return (
    <>
      <NwssDrawerHeader signal={signal} />
      <NwssSamplesSection signal={signal} />
    </>
  )
}

function DetailMessage({
  color,
  children,
}: {
  color: string
  children: React.ReactNode
}) {
  return (
    <Typography variant="body2" sx={{ color, textAlign: "center", py: 6 }}>
      {children}
    </Typography>
  )
}
